import { createContext, useContext, useState, type ReactNode } from 'react';

interface SessionContextValue {
  jwtToken:            string | null;
  setJwtToken:         (token: string) => void;
  tokenValidated:      boolean;
  identityVerified:    boolean;
  sessionToken:        string | null;
  setTokenValidated:   (validated: boolean) => void;
  setIdentityVerified: (verified: boolean, sessionToken: string) => void;
  formAccessId:        string | null;
  setFormAccessId:     (id: string) => void;
  resetSession:        () => void;
}

const SK = {
  jwtToken:         'aca_jwt',
  tokenValidated:   'aca_tv',
  identityVerified: 'aca_iv',
  sessionToken:     'aca_st',
  formAccessId:     'aca_fai',
} as const;

function read(key: string): string | null {
  return sessionStorage.getItem(key);
}

function save(key: string, value: string | null): void {
  if (value === null) sessionStorage.removeItem(key);
  else sessionStorage.setItem(key, value);
}

const SessionContext = createContext<SessionContextValue | undefined>(undefined);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [jwtToken,         setJwtTokenState]         = useState<string | null>(() => read(SK.jwtToken));
  const [tokenValidated,   setTokenValidatedState]   = useState<boolean>(() => read(SK.tokenValidated) === 'true');
  const [identityVerified, setIdentityVerifiedState] = useState<boolean>(() => read(SK.identityVerified) === 'true');
  const [sessionToken,     setSessionTokenState]     = useState<string | null>(() => read(SK.sessionToken));
  const [formAccessId,     setFormAccessIdState]     = useState<string | null>(() => read(SK.formAccessId));

  const setJwtToken = (token: string) => {
    save(SK.jwtToken, token);
    save(SK.tokenValidated, 'true');
    setJwtTokenState(token);
    setTokenValidatedState(true);
  };

  const setTokenValidated = (validated: boolean) => {
    save(SK.tokenValidated, String(validated));
    setTokenValidatedState(validated);
  };

  const setIdentityVerified = (verified: boolean, token: string) => {
    save(SK.identityVerified, String(verified));
    save(SK.sessionToken, token);
    setIdentityVerifiedState(verified);
    setSessionTokenState(token);
  };

  const setFormAccessId = (id: string) => {
    save(SK.formAccessId, id);
    setFormAccessIdState(id);
  };

  const resetSession = () => {
    Object.values(SK).forEach(k => sessionStorage.removeItem(k));
    setJwtTokenState(null);
    setTokenValidatedState(false);
    setIdentityVerifiedState(false);
    setSessionTokenState(null);
    setFormAccessIdState(null);
  };

  return (
    <SessionContext.Provider
      value={{
        jwtToken,
        setJwtToken,
        tokenValidated,
        identityVerified,
        sessionToken,
        setTokenValidated,
        setIdentityVerified,
        formAccessId,
        setFormAccessId,
        resetSession,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
}

export function useSession(): SessionContextValue {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
}
